import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api';
import PrivacyPolicy from '../components/PrivacyPolicy';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { requestPushPermission, refreshPushToken } from '../firebase';
import { Sheet, plural } from '../components/AdminUi';
import { useTasks } from '../context/TasksContext';
import { User, KeyRound, Bell, Shield, LogOut, ChevronRight } from 'lucide-react';

// Profil („Moj DVD”): moja statistika, moje grupe, postavke računa i uređaja.
// Uređivanje imena / e-maila u panelu s Odustani / Spremi.

// Isto kao avatar na Početnoj (Home.js)
const AVATAR_COLORS = ['#0984e3', '#6c5ce7', '#00b894', '#e17055', '#fd79a8', '#fdcb6e', '#74b9ff', '#a29bfe'];
const avatarColor = (name) => AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length];
const initialsOf = (name) => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
const ROLE_LABEL = { admin: 'Admin', manager: 'Upravitelj', editor: 'Editor', viewer: 'Član' };
const TILE_COLORS = ['#3a6ea3', '#4f9d84', '#8a6fae', '#b0693f', '#5b7fb0', '#a8781f'];
const groupInitials = (name) => {
  const words = (name || '?').trim().split(/\s+/);
  return (words.length > 1 ? words.map(w => w[0]).join('') : words[0]).slice(0, 2).toUpperCase();
};

function PersonalSheet({ user, onClose, onSaved }) {
  const [fullName, setFullName] = useState(user.full_name || '');
  const [email, setEmail] = useState(user.email || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const closeRef = useRef(null);
  const dirty = fullName !== (user.full_name || '') || email !== (user.email || '');
  const missing = !fullName.trim() ? 'Upiši ime i prezime' : !email.trim() ? 'Upiši e-mail'
    : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? 'E-mail nije ispravan' : null;

  const save = async () => {
    if (missing || !dirty) return;
    setBusy(true); setError('');
    try {
      const res = await api.put('/me', { full_name: fullName.trim(), email: email.trim() });
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Spremanje nije uspjelo.');
      setBusy(false);
    }
  };

  return (
    <Sheet onClose={onClose} dirty={dirty} busy={busy} closeRef={closeRef}
      head={<div className="adm-sh"><div className="adm-main"><h3>Osobni podaci</h3></div></div>}
      footer={
        <div className="adm-dirty">
          {error && <p style={{ color: '#ac463b' }}>{error}</p>}
          <div className="tf-foot">
            <button type="button" className="adm-btn" disabled={busy} onClick={() => closeRef.current && closeRef.current()}>Odustani</button>
            <button type="button" className={`adm-btn ${missing ? 'tf-off' : 'primary'}`} disabled={!!missing || !dirty || busy} onClick={save}>
              {busy ? 'Spremam…' : missing || 'Spremi'}
            </button>
          </div>
        </div>
      }
    >
      <label className="adm-fl" htmlFor="pf-name">Ime i prezime</label>
      <input id="pf-name" className="adm-input" value={fullName} maxLength={100} autoComplete="name"
        onChange={e => { setFullName(e.target.value); setError(''); }} />
      <label className="adm-fl" htmlFor="pf-email">E-mail</label>
      <input id="pf-email" className="adm-input" type="email" value={email} maxLength={254} autoComplete="email"
        onChange={e => { setEmail(e.target.value); setError(''); }} />
      <p className="adm-note">
        E-mailom se prijavljuješ u aplikaciju. Ako ga promijeniš, sljedeći put se prijavi novim (kod prijave s Googleom — Google računom s tom adresom).
        {email.trim() !== (user.email || '') && ' Na ostalim uređajima bit ćeš odjavljen/a.'}
      </p>
    </Sheet>
  );
}

function ProfilePage({ user, onUserUpdate, navigate, setDashboardFilter, setDashboardParticipation, setInterventionParticipation, onOpenGroup, onLogout }) {
  const { tasksByYear, fetchYear } = useTasks();
  const year = new Date().getFullYear();
  const [interventions, setInterventions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [toast, setToast] = useState('');

  const pushSupported = 'Notification' in window && 'serviceWorker' in navigator;
  const [pushPermission, setPushPermission] = useState(() => (pushSupported ? Notification.permission : 'unsupported'));
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');

  useEffect(() => {
    fetchYear(year).catch(() => {});
    api.get(`/interventions?year=${year}`).then(r => setInterventions(r.data || [])).catch(() => {});
    api.get('/groups').then(r => setGroups(r.data || [])).catch(() => {});
  }, [year, fetchYear]);

  const showToast = (text) => { setToast(text); setTimeout(() => setToast(''), 3000); };

  // Moja statistika (prije na Početnoj)
  const allTasks = useMemo(() => tasksByYear[year] || [], [tasksByYear, year]);
  const stats = useMemo(() => ({
    active: allTasks.filter(t => t.status !== 'done' && t.status !== 'proposed' && t.assignments?.some(a => a.email === user.email && a.status !== 'declined')).length,
    done: allTasks.filter(t => t.status === 'done' && t.assignments?.some(a => a.email === user.email)).length,
    interventions: interventions.filter(i => i.participants?.some(p => p.email === user.email)).length,
  }), [allTasks, interventions, user.email]);

  // Moje grupe: one u kojima sam član, vlasničke prve
  const myGroups = useMemo(() => {
    const roleOf = new Map((user.groups || []).map(g => [g.id, g.group_role]));
    return groups
      .filter(g => roleOf.has(g.id) || g.members?.some(m => m.email === user.email))
      .map(g => ({ ...g, myRole: roleOf.get(g.id) || g.members?.find(m => m.email === user.email)?.role || 'member' }))
      .sort((a, b) => (a.myRole === 'owner' ? 0 : 1) - (b.myRole === 'owner' ? 0 : 1) || a.name.localeCompare(b.name, 'hr'));
  }, [groups, user]);

  const handlePush = async () => {
    if (pushBusy || pushPermission === 'denied') return;
    setPushBusy(true); setPushError('');
    try {
      const token = Notification.permission === 'granted' ? await refreshPushToken() : await requestPushPermission();
      setPushPermission(Notification.permission);
      if (!token) { setPushError('Obavijesti nisu uključene — dozvola nije dana.'); return; }
      await api.post('/push/subscribe', { token });
      localStorage.setItem('push_token', token);
      showToast(pushPermission === 'granted' ? 'Obavijesti su ponovno povezane s ovim uređajem.' : 'Obavijesti su uključene.');
    } catch (err) {
      setPushError(err.message || 'Uključivanje obavijesti nije uspjelo.');
    } finally {
      setPushBusy(false);
    }
  };
  const pushState = pushPermission === 'granted' ? { text: 'uključene', cls: 'ok' }
    : pushPermission === 'denied' ? { text: 'blokirane', cls: 'bad' }
      : { text: 'isključene', cls: 'off' };

  const name = user.full_name || user.email;
  const goStat = (kind) => {
    if (kind === 'interventions') { setInterventionParticipation && setInterventionParticipation('participant'); navigate && navigate('interventions'); return; }
    setDashboardFilter && setDashboardFilter(kind === 'done' ? 'done' : 'all');
    setDashboardParticipation && setDashboardParticipation('assigned');
    navigate && navigate('dashboard');
  };

  return (
    <div className="pf-page">
      <div className="pf-head">
        <div className="pf-av" style={{ background: avatarColor(name) }}>{initialsOf(name)}</div>
        <div style={{ minWidth: 0 }}>
          <div className="pf-name">{user.full_name || 'Korisnik'}</div>
          <div className="pf-sub">{ROLE_LABEL[user.role] || 'Član'} · {user.email}</div>
        </div>
      </div>

      <div className="adm-lbl">Moja {year}.</div>
      <div className="pf-stats">
        <button type="button" className="pf-st" onClick={() => goStat('active')}><b className="tab">{stats.active}</b><span>Aktivnih</span></button>
        <button type="button" className="pf-st" onClick={() => goStat('done')}><b className="tab">{stats.done}</b><span>Završenih</span></button>
        <button type="button" className="pf-st" onClick={() => goStat('interventions')}><b className="tab">{stats.interventions}</b><span>Intervencija</span></button>
      </div>

      {myGroups.length > 0 && <>
        <div className="adm-lbl">Moje grupe</div>
        <div className="adm-card">
          {myGroups.map((g, i) => {
            const n = g.members?.length || 0;
            return (
              <button key={g.id} type="button" className="pf-row" onClick={() => onOpenGroup && onOpenGroup(g.id)}>
                <span className="pf-tile" style={{ background: TILE_COLORS[g.id % TILE_COLORS.length] }}>{groupInitials(g.name)}</span>
                <span className="pf-k">{g.name}<small>{g.myRole === 'owner' ? 'vlasnik grupe' : 'član'} · {n} {plural(n, 'član', 'člana', 'članova')}</small></span>
                <ChevronRight size={17} color="#b2bec3" />
              </button>
            );
          })}
        </div>
      </>}

      <div className="adm-lbl">Postavke</div>
      <div className="adm-card">
        <button type="button" className="pf-row" onClick={() => setEditOpen(true)}>
          <span className="pf-ic"><User size={18} /></span>
          <span className="pf-k">Osobni podaci<small>Ime i e-mail</small></span>
          <ChevronRight size={17} color="#b2bec3" />
        </button>
        <button type="button" className="pf-row" onClick={() => setShowChangePassword(true)}>
          <span className="pf-ic"><KeyRound size={18} /></span>
          <span className="pf-k">Lozinka</span>
          <ChevronRight size={17} color="#b2bec3" />
        </button>
        {pushSupported && (
          <button type="button" className="pf-row" onClick={handlePush} disabled={pushBusy || pushPermission === 'denied'}>
            <span className="pf-ic"><Bell size={18} /></span>
            <span className="pf-k">Obavijesti na ovom uređaju
              <small>{pushPermission === 'granted' ? 'Dodirni ako prestanu stizati' : pushPermission === 'denied' ? 'Uključi ih u postavkama preglednika' : 'Dodirni za uključivanje'}</small>
            </span>
            <span className={`pf-state ${pushState.cls}`}>{pushBusy ? '…' : pushState.text}</span>
          </button>
        )}
        <button type="button" className="pf-row" onClick={() => setShowPrivacy(true)}>
          <span className="pf-ic"><Shield size={18} /></span>
          <span className="pf-k">Politika privatnosti</span>
          <ChevronRight size={17} color="#b2bec3" />
        </button>
        {onLogout && (
          <button type="button" className="pf-row red" onClick={onLogout}>
            <span className="pf-ic"><LogOut size={18} /></span>
            <span className="pf-k">Odjava</span>
          </button>
        )}
      </div>
      {pushError && <p className="adm-err" style={{ marginTop: 8 }}>{pushError}</p>}

      {editOpen && (
        <PersonalSheet user={user} onClose={() => setEditOpen(false)}
          onSaved={(data) => {
            // PUT /me ne vraća grupe — zadrži ih
            onUserUpdate && onUserUpdate({ ...user, ...data });
            setEditOpen(false);
            showToast('Podaci su spremljeni.');
          }} />
      )}
      {toast && <div className="pf-toast" role="status">{toast}</div>}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
    </div>
  );
}

export default ProfilePage;
